import type { VoidFunctionComponent } from 'react';

import { isOrgInFreeTier } from '../../../../helpers/subscriptions';
import Button, { BUTTON_SIZE, BUTTON_TYPE } from '../../../generic/Button';
import PaidFeatureGate from '../../../generic/Gates/PaidFeatureGate';
import { icons, IconText } from '../../../generic/Icon';
import Link from '../../../generic/Link';
import Header from '../../../shared/settings/SettingsPageHeader';
import Section from '../../../shared/settings/SettingsPageSection';
import OrganizationSettingsPermissionGate from '../../OrganizationSettingsPermissionGate';
import { shouldDisplayCustomLicenseScanSettings } from '../../shared/permissions';

import CustomLicenseScanConfigRow from './CustomLicenseScanConfigRow';
import useCustomLicenseScanSettings from './useCustomLicenseScanSettings';

/**
 * Settings section for the custom license scanning feature.
 */
const CustomLicenseScanSettings: VoidFunctionComponent = () => {
  const {
    customLicenseScanConfigs,
    isLoading,
    onAdd,
    onRemove,
    onUpdate,
    onSaveSettings,
    isSaveButtonDisabled,
  } = useCustomLicenseScanSettings();

  const isFreeOrg = isOrgInFreeTier();

  return (
    <PaidFeatureGate isRestricted={isFreeOrg} hidePill>
      <Header heading="Custom License Scan Settings">
        <Header.SaveButton
          onClick={onSaveSettings}
          isDisabled={isSaveButtonDisabled}
          isLoading={isLoading.save}
        />
      </Header>
      <Section>
        <Section.Left heading="Configurations">
          <Section.Paragraph>
            Custom License Scans search through your codebase for licenses defined by regular expressions
            {' '}
            that you provide in the &quot;Match Criteria&quot; column in the form on the right.
          </Section.Paragraph>
          <Section.Paragraph>
            Any custom license scans defined here will be run every time anyone in your organization
            {' '}
            runs <code>fossa analyze</code>.
          </Section.Paragraph>
          <Section.Paragraph>
            The value in the &quot;Name&quot; column will be used in the FOSSA UI and in reports when
            {' '}
            the corresponding custom license is found.
          </Section.Paragraph>
          <Section.Paragraph>
            For more information, see
            {' '}
            the <Link to="https://github.com/fossas/fossa-cli/blob/master/docs/features/custom-license-and-keyword-searches.md" openInNewTab>Custom License Scan documentation</Link>.
          </Section.Paragraph>
        </Section.Left>
        <Section.Right>
          {customLicenseScanConfigs?.map((config, index) => (
            <CustomLicenseScanConfigRow
              key={config.id || index}
              name={config.name}
              matchCriteria={config.matchCriteria}
              index={index}
              onUpdateName={name => onUpdate(index, 'name', name)}
              onUpdateMatchCriteria={matchCriteria => onUpdate(index, 'matchCriteria', matchCriteria)}
              onRemove={onRemove}
            />
          ))}
          <Button
            type={BUTTON_TYPE.SECONDARY}
            size={BUTTON_SIZE.MEDIUM}
            onClick={onAdd}
          >
            <IconText icon={icons.plus} text="Add" />
          </Button>
        </Section.Right>
      </Section>
    </PaidFeatureGate>
  );
};
/**
 * CustomLicenseScanSettingsContainer displays the organization custom license scan settings page if the user has permission to view it.
 */
const CustomLicenseScanSettingsContainer: VoidFunctionComponent = () => (
  <OrganizationSettingsPermissionGate checkPermission={shouldDisplayCustomLicenseScanSettings}>
    <CustomLicenseScanSettings />
  </OrganizationSettingsPermissionGate>
);

export default CustomLicenseScanSettingsContainer;
